import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Municipality } from '@/components/admin/users/types';
import { useAuthState } from '@/hooks/useAuthState';
import { useMunicipalities } from '@/hooks/useMunicipalities';

interface MunicipalityContextType {
  currentMunicipality: Municipality | null;
  allMunicipalities: Municipality[];
  setCurrentMunicipality: (municipality: Municipality | null) => void;
  isSuperAdmin: boolean;
  isLoading: boolean;
  isShowingAllMunicipalities: boolean;
}

const MunicipalityContext = createContext<MunicipalityContextType | undefined>(undefined);

export const MunicipalityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useAuthState();
  const [currentMunicipality, setCurrentMunicipality] = useState<Municipality | null>(null);

  // Check if user is super admin
  const { data: isSuperAdmin = false } = useQuery({
    queryKey: ['is-super-admin', user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase.rpc('is_super_admin', {
        user_id_param: user.id
      });
      return data || false;
    },
    enabled: !!user,
  });

  // Fetch all municipalities using the shared hook
  const { data: allMunicipalities = [], isLoading } = useMunicipalities();

  // Set initial municipality based on user's profile or first available
  useEffect(() => {
    if (!isLoading && allMunicipalities.length > 0 && !currentMunicipality) {
      if (isSuperAdmin) {
        // Super admins default to "All municipalities" (null)
        setCurrentMunicipality(null);
      } else if (profile?.municipality_id) {
        const userMunicipality = allMunicipalities.find(m => m.id === profile.municipality_id);
        if (userMunicipality) {
          setCurrentMunicipality(userMunicipality);
        }
      }
      // Note: If profile doesn't have municipality_id or is not loaded yet, 
      // we don't set a default - wait for profile to load
    }
  }, [allMunicipalities, profile, isLoading, currentMunicipality, isSuperAdmin]);

  const isShowingAllMunicipalities = currentMunicipality === null;

  return (
    <MunicipalityContext.Provider
      value={{
        currentMunicipality,
        allMunicipalities,
        setCurrentMunicipality,
        isSuperAdmin,
        isLoading,
        isShowingAllMunicipalities,
      }}
    >
      {children}
    </MunicipalityContext.Provider>
  );
};

export const useMunicipality = () => {
  const context = useContext(MunicipalityContext);
  if (context === undefined) {
    throw new Error('useMunicipality must be used within a MunicipalityProvider');
  }
  return context;
};
